import Button from '@carvana/ds-button';
import React, { useCallback, useContext, useState } from 'react';
import { UserInitializationContext } from '|/context';
import { currency } from '|/helpers';
import useAnalyticsContext from '|/hooks/use-analytics-context';
import { Consents } from './consents';
import { DigitalWallets } from './digital-wallets';
import { ImpersonationBlockedBanner } from './impersonation-blocked-banner';
import styles from './pay-at-order.module.css';
import { PaymentDetailsCard } from './payment-details-card';
import { usePlaidModal } from './plaid-modal';
import { isDigitalWalletSelection, isPayLaterSelection } from './utils/digital-wallet';
import { usePaymentContext } from '../../contexts/payment';
import { collectPayment, fetchPaymentSummary, submitConsents } from '../../services';

const getButtonText = (amountDue: number, isPayLater: boolean) =>
  amountDue > 0 && !isPayLater
    ? `Pay ${currency(amountDue).replace('.00', '')} now`
    : 'Place order';

interface PayAtOrderProps {
  onSubmit: () => Promise<boolean>;
  onError: () => void;
  editPaymentMethod: () => void;
}

export const PayAtOrder = ({ onSubmit, onError, editPaymentMethod }: PayAtOrderProps) => {
  const {
    state: {
      paymentSummary: {
        shouldDisableWhileImpersonating,
        shouldInitiateRequestForPayment,
        shouldCollectPayment,
        requestForPaymentAmount,
        isCardEligible,
        purchasePaymentBreakdown,
        paymentSelection,
        consents
      }
    },
    dispatch: paymentDispatch
  } = usePaymentContext();

  const { accessToken } = useContext(UserInitializationContext);
  const { trackEvent } = useAnalyticsContext();

  const [hasCustomerConfirmedSufficientFunds, setHasCustomerConfirmedSufficientFunds] =
    useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDigitalWalletSupported, setIsDigitalWalletSupported] = useState(false);

  const isUserBeingImpersonated = accessToken?.user.is_impersonating.toLowerCase() === 'true';
  const shouldUseDigitalWallet = isDigitalWalletSelection(paymentSelection);
  const isPayLater = isPayLaterSelection(paymentSelection);
  const isDigitalWalletAvailable = isDigitalWalletSupported && isCardEligible;
  const shouldBlockImpersonatedSubmit = shouldDisableWhileImpersonating && !isPayLater;
  const shouldShowConsents = !shouldUseDigitalWallet || isDigitalWalletAvailable;
  const shouldShowImpersonationText =
    isUserBeingImpersonated && (shouldBlockImpersonatedSubmit || shouldUseDigitalWallet);

  const isButtonDisabled =
    isSubmitting || !hasCustomerConfirmedSufficientFunds || shouldShowImpersonationText;

  const isButtonLoading = isSubmitting;
  const buttonText = getButtonText(purchasePaymentBreakdown.amountDue, isPayLater);

  const handlePaoSubmit = async () => {
    await onSubmit();
  };

  const handlePaoError = () => {
    onError();
  };

  const { plaidModal, openPlaidModal } = usePlaidModal({
    paymentAmount: requestForPaymentAmount ?? 0,
    onSuccess: handlePaoSubmit,
    onExit: () => {
      /* re-show PAO inline — no-op since it's already visible */
    },
    onError: handlePaoError
  });

  const onCardAdded = useCallback(async () => {
    const summary = await fetchPaymentSummary();
    paymentDispatch({
      type: 'SET_IS_CARD_ELIGIBLE',
      payload: summary.isCardEligible
    });
    return summary;
  }, [paymentDispatch]);

  const submitPayAtOrder = async (): Promise<boolean> => {
    setIsSubmitting(true);

    const consentTypes = consents.map(({ consentType, consentSubType }) => ({
      consentType,
      consentSubType
    }));

    trackEvent('pp_complete_purchase_pao_submit', {
      isUserBeingImpersonated,
      consentTypes,
      shouldInitiateRequestForPayment,
      shouldCollectPayment,
      requestForPaymentAmount,
      purchasePaymentBreakdown,
      paymentSelection,
      buttonText
    });

    try {
      if (isUserBeingImpersonated) {
        if (shouldBlockImpersonatedSubmit) {
          throw new Error('Disabled while impersonating');
        }

        const didNavigate = await onSubmit();
        setIsSubmitting(didNavigate);
        return didNavigate;
      }

      if (shouldCollectPayment) {
        const { paymentCollectionSucceeded } = await collectPayment(consentTypes);

        if (!paymentCollectionSucceeded) {
          editPaymentMethod();
          return true;
        }

        const didNavigate = await onSubmit();
        setIsSubmitting(didNavigate);
        return didNavigate;
      }

      if (consentTypes.length > 0) {
        await submitConsents(consentTypes);
      }

      if (shouldInitiateRequestForPayment) {
        openPlaidModal();
        setIsSubmitting(false);
        return true;
      }

      const didNavigate = await onSubmit();
      setIsSubmitting(didNavigate);
      return didNavigate;
    } catch {
      setIsSubmitting(false);
      onError();
      return false;
    }
  };

  return (
    <>
      <div className={styles.container}>
        <div className={styles.contentContainer}>
          <PaymentDetailsCard />

          <div className={styles.belowCard}>
            {shouldShowConsents && (
              <Consents
                disableClickOnSufficientFundsCheckbox={isSubmitting}
                hasCustomerConfirmedSufficientFunds={hasCustomerConfirmedSufficientFunds}
                setHasCustomerConfirmedSufficientFunds={setHasCustomerConfirmedSufficientFunds}
              />
            )}

            {shouldShowImpersonationText && (
              <ImpersonationBlockedBanner
                shouldUseDigitalWallet={shouldUseDigitalWallet}
                shouldInitiateRequestForPayment={shouldInitiateRequestForPayment}
                selectionType={paymentSelection?.selectionType}
              />
            )}

            {shouldUseDigitalWallet && !isUserBeingImpersonated && (
              <DigitalWallets
                digitalWallet={paymentSelection?.digitalWallet}
                isCardEligible={isCardEligible}
                editPaymentMethod={editPaymentMethod}
                onSupportChange={setIsDigitalWalletSupported}
                onCardAdded={onCardAdded}
                onSetupConfirmed={submitPayAtOrder}
                onError={onError}
              />
            )}

            {!shouldUseDigitalWallet && (
              <div className={styles.buttonContainer}>
                <Button
                  variant="primary"
                  size="md"
                  width="full"
                  disabled={isButtonDisabled}
                  isLoading={isButtonLoading}
                  onClick={() => {
                    submitPayAtOrder();
                  }}
                  data-analytics-id="pp_complete_purchase_pao_submit"
                >
                  {buttonText}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {plaidModal}
    </>
  );
};
